import { useCallback } from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import styled, { useTheme } from 'styled-components';

export const StyledWrapper = styled.div<{
  $hasHorizontalPadding: boolean;
  $hasVerticalPadding: boolean;
}>(({
  $hasHorizontalPadding,
  $hasVerticalPadding,

  theme: {
    colors,
    indents,
    repeat,
  },
}) => ({
  position: 'relative',
  paddingTop: $hasVerticalPadding ? indents.INDENT_2 : 0,
  paddingRight: $hasHorizontalPadding ? indents.INDENT_2 : 0,
  paddingBottom: $hasVerticalPadding ? indents.INDENT_3 : 0,
  paddingLeft: $hasHorizontalPadding ? indents.INDENT_2 : 0,
  backgroundColor: colors.NEUTRAL_BACKGROUND,
  borderRadius: 4,

  '&::before': {
    content: repeat.WITH_LEFT_LINE ? '""' : undefined,
    position: 'absolute',
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    backgroundColor: colors[repeat.BACKGROUND_COLOR],
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },

  '& + &': {
    marginTop: indents.INDENT_2,
  },
}));

export const StyledTitle = styled.div<{
  $hasHorizontalPadding: boolean;
}>(({
  $hasHorizontalPadding,

  theme: {
    fonts,
    indents,
  },
}) => ({
  ...fonts.H5,
  paddingRight: $hasHorizontalPadding ? 0 : indents.INDENT_2,
  paddingLeft: $hasHorizontalPadding ? 0 : indents.INDENT_2,
  marginBottom: indents.INDENT_3,
}));

export const StyledRemoveButton = styled.button<{
  disabled: boolean;
}>(({
  disabled,

  theme: {
    colors,
  },
}) => ({
  position: 'absolute',
  top: 12,
  right: 8,
  color: disabled ? colors.NEUTRAL_INACTIVE : colors.NEUTRAL_SECONDARY,
  cursor: disabled ? 'default' : 'pointer',
  fontSize: 15,
  padding: 0,
  margin: 0,
  backgroundColor: 'transparent',
  border: 'none',
  outline: 'none',

  '&:hover': disabled ? undefined : {
    color: colors.ACTION_HOVER,
  },

  '&:active': disabled ? undefined : {
    color: colors.ACTION_PRESSED,
  },
}));

export type RepeatBlockProps = {
  /**
   * Выключена ли кнопка удаления
   */
  disabled?: boolean;

  /**
   * Заголовок
   */
  title?: ReactNode;

  /**
   * Индекс в последовательности блоков
   */
  index: number;

  /**
   * Есть ли отступ от левой и правой границ до контента
   */
  hasHorizontalPadding?: boolean;

  /**
   * Есть ли отступ от верхней и нижней границ до контента
   */
  hasVerticalPadding?: boolean;

  /**
   * Обработчик нажания на кнопку удаления, если не определён, кнопка удаления не отображается
   * @param {number} index - индекс блока в массиве
   */
  handleRemove?: (index: number) => void;

  /**
   * Кастомная кнопка удаления
   */
  renderRemoveButton?: (disabled: boolean, onRemoveButtonClick: () => void) => ReactNode;

  children?: ReactNode;
};

export function RepeatBlock({
  disabled = false,
  title = undefined,
  index,
  hasHorizontalPadding = true,
  hasVerticalPadding = true,
  handleRemove = undefined,
  renderRemoveButton = undefined,
  children = undefined,
}: RepeatBlockProps): ReactElement {
  const onRemoveButtonClick = useCallback(() => {
    if (handleRemove) {
      handleRemove(index);
    }
  }, [
    index,
    handleRemove,
  ]);

  const { repeat } = useTheme();

  return (
    <StyledWrapper
      $hasHorizontalPadding={hasHorizontalPadding}
      $hasVerticalPadding={hasVerticalPadding}
    >
      {
        handleRemove && (
          renderRemoveButton ? (
            renderRemoveButton(disabled, onRemoveButtonClick)
          ) : (
            <StyledRemoveButton
              type="button"
              disabled={disabled}
              onClick={onRemoveButtonClick}
            >
              {repeat.REMOVE_ICON}
            </StyledRemoveButton>
          )
        )
      }

      {
        title && (
          <StyledTitle
            $hasHorizontalPadding={hasHorizontalPadding}
          >
            {title}
          </StyledTitle>
        )
      }

      {children}
    </StyledWrapper>
  );
}
